declare global {
  interface HTMLElementTagNameMap {
    "cart-size-modal": CartSizeModal;
  }
}

type OptionWithValue = { name: string; value: string };

type CartItem = {
  product_id: number;
  variant_id?: number;
  id?: number;
  options_with_values?: OptionWithValue[];
};

type JQueryLike = ((selector: string) => {
  on: (event: string, handler: () => void) => void;
}) & {
  fn?: unknown;
};

const COLOR_OPTION_NAMES = ["color", "colour"];

class CartSizeModal extends HTMLElement {
  private overlay: HTMLElement | null = null;
  private closeButton: HTMLElement | null = null;
  private continueButton: HTMLElement | null = null;
  private sizeGuideButton: HTMLElement | null = null;

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.isOpen) {
      this.close();
    }
  };

  connectedCallback(): void {
    this.overlay = this.querySelector("[data-cart-size-modal-overlay]");
    this.closeButton = this.querySelector("[data-cart-size-modal-close]");
    this.continueButton = this.querySelector("[data-cart-size-modal-continue]");
    this.sizeGuideButton = this.querySelector(
      "[data-cart-size-modal-size-guide]",
    );

    this.overlay?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.close();
    });
    this.closeButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    });
    this.continueButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    });
    this.sizeGuideButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openSizeGuide();
    });

    document.addEventListener("keydown", this.onKeyDown);

    this.bindAddToCartEvent();

    if (document.body.classList.contains("template-cart")) {
      this.checkCart();
    }
  }

  disconnectedCallback(): void {
    document.removeEventListener("keydown", this.onKeyDown);
  }

  get isOpen(): boolean {
    return this.classList.contains("is-open");
  }

  open(): void {
    this.classList.add("is-open");
  }

  close(): void {
    this.classList.remove("is-open");
  }

  private openSizeGuide(): void {
    this.close();
    document.body.removeAttribute("data-cart-active");

    const drawer = document.querySelector("size-chart-drawer") as
      | (HTMLElement & { open?: () => void })
      | null;
    drawer?.open?.();
  }

  private bindAddToCartEvent(): void {
    const tryBind = (): boolean => {
      const jq = (window as unknown as { jQuery?: JQueryLike }).jQuery;
      if (!jq) return false;
      jq("body").on("added.ajaxProduct", () => this.checkCart());
      return true;
    };

    if (tryBind()) return;

    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts++;
      if (tryBind() || attempts > 50) window.clearInterval(interval);
    }, 100);
  }

  private async checkCart(): Promise<void> {
    try {
      const res = await fetch("/cart.js", {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const cart = (await res.json()) as { items?: CartItem[] };
      if (this.hasMultipleSizes(cart.items ?? [])) this.open();
    } catch {
      // ignore network errors
    }
  }

  private hasMultipleSizes(items: CartItem[]): boolean {
    const variantsByProductColor = new Map<string, Set<number>>();
    for (const item of items) {
      const color =
        item.options_with_values?.find((o) =>
          COLOR_OPTION_NAMES.includes(o.name?.toLowerCase() ?? ""),
        )?.value ?? "";
      const variantId = item.variant_id ?? item.id ?? 0;
      const key = `${item.product_id}::${color}`;
      let variants = variantsByProductColor.get(key);
      if (!variants) {
        variants = new Set();
        variantsByProductColor.set(key, variants);
      }
      variants.add(variantId);
      if (variants.size > 1) return true;
    }
    return false;
  }
}

if (!window.customElements.get("cart-size-modal")) {
  window.customElements.define("cart-size-modal", CartSizeModal);
}

export { CartSizeModal };
